import {
  Box,
  Button,
  chakra,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
//  useColorModeValue,
} from "@chakra-ui/react";
import { useState, FormEvent } from "react";

interface MollieMandateFormProps {
    companyName: string;
    planLabel: string;
    onSuccess: (consumerAccount: string, consumerName: string) => Promise<void>;
}

export default function MollieMandateForm(props: MollieMandateFormProps) {
    const { companyName, planLabel, onSuccess } = props;
    const [consumerName, setConsumerName] = useState<string>("");
    const [consumerAccount, setConsumerAccount] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        consumerName: null,
        consumerAccount: null,
    });
    const [formError, setFormError] = useState<string | null>(null);
    const [mandateAccepted, setMandateAccepted] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const borderColor = "gray.600"; // useColorModeValue("gray.200", "gray.600");
    const mandateBg = "orange.900"; // useColorModeValue("orange.50", "orange.900");
    const mandateText = "orange.100"; // useColorModeValue("orange.800", "orange.100");
    const mandateBorder = "orange.600"; // useColorModeValue("orange.300", "orange.600");

    const isSubmitDisabled: boolean = !consumerName || !consumerAccount || !mandateAccepted || loading;


    const formatIBAN = (value: string): string => {
        return value
            .replace(/\s/g, "")
            .toUpperCase()
            .replace(/(.{4})/g, "$1 ")
            .trim();
    }

    function handleIBANChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const formatted = formatIBAN(e.target.value);
        setConsumerAccount(formatted);
        if (fieldErrors.consumerAccount) {
            setFieldErrors((prev) => ({ ...prev, consumerAccount: null }));
        }
    }

    function validateFields(): boolean {
        const errors: FieldErrors = {
            consumerName: null,
            consumerAccount: null,
        };

        if (consumerName.trim().length === 0) {
            errors.consumerName = "Le nom du titulaire est requis.";
        }

        const rawIBAN = consumerAccount.replace(/\s/g, "");
        if (rawIBAN.length === 0) {
            errors.consumerAccount = "L'IBAN est requis.";
        } else if (!isValidIBAN(rawIBAN)) {
            errors.consumerAccount = "L'IBAN saisi n'est pas valide.";
        }

        setFieldErrors(errors);
        return !errors.consumerName && !errors.consumerAccount;
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        if (!mandateAccepted || !validateFields()) { return; }

        setFormError(null);
        setLoading(true);

        try {
            const rawIBAN = consumerAccount.replace(/\s/g, "");
            await onSuccess(rawIBAN, consumerName);
    //   } else {
    //     // Le backend appelle POST /v2/customers/{id}/mandates
    //     // avec { method: "directdebit", consumerAccount: rawIBAN, consumerName }
    //     await fetch("/api/subscriptions/create-mandate", {
    //       method: "POST",
    //       headers: { "Content-Type": "application/json" },
    //       body: JSON.stringify({
    //         customerId,
    //         consumerAccount: rawIBAN,
    //         consumerName,
    //       }),
    //     });
    //   }
        } catch {
            setFormError("Une erreur inattendue est survenue.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <chakra.form p={6} borderRadius="xl" border="1px solid" borderColor={borderColor} bg="prussianBlue.500" onSubmit={handleSubmit}>
            <HStack justify="space-between" mb={5} width="100%">
                <VStack align="start" spacing={0}>
                    <Text fontWeight="600" fontSize="lg">Prélèvement SEPA</Text>
                    <Text fontSize="sm" color="gray.500">{planLabel}</Text>
                </VStack>
                <Badge colorScheme="orange" fontSize="xs" px={2} py={1} borderRadius="md">Mandat SEPA</Badge>
            </HStack>

            <VStack spacing={4} width="100%">
                <FormControl isRequired isInvalid={!!fieldErrors.consumerName}>
                    <FormLabel fontSize="sm">Titulaire du compte bancaire</FormLabel>
                    <Input value={consumerName} size="md" onChange={(e) => {
                            setConsumerName(e.target.value);
                            if (fieldErrors.consumerName) {
                                setFieldErrors((prev) => ({ ...prev, consumerName: null }));
                            }
                        }}
                    />
                    <FormErrorMessage>{fieldErrors.consumerName}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!fieldErrors.consumerAccount}>
                    <FormLabel fontSize="sm">IBAN</FormLabel>
                    <Input value={consumerAccount} placeholder="FR76 3000 6000 0112 3456 7890 189" size="md" fontFamily="mono" letterSpacing="0.05em" maxLength={42} onChange={handleIBANChange} />
                    <FormErrorMessage>{fieldErrors.consumerAccount}</FormErrorMessage>
                </FormControl>
            </VStack>

            <Box mt={5} p={4} bg={mandateBg} border="1px solid" borderColor={mandateBorder} borderRadius="md">
                <Text fontSize="xs" color={mandateText} lineHeight="1.7" mb={3}>
                    En signant ce formulaire de mandat, vous autorisez{" "}
                    <Text as="span" fontWeight="600">{companyName}</Text>{" "}
                    à envoyer des instructions à votre banque pour débiter votre compte,
                    conformément aux instructions de {companyName}. Vous bénéficiez d'un
                    droit au remboursement par votre banque selon les modalités décrites
                    dans la convention que vous avez passée avec elle. Toute demande de
                    remboursement doit être présentée dans les 8 semaines suivant la date
                    de débit de votre compte.
                </Text>
                <Checkbox isChecked={mandateAccepted} onChange={(e) => setMandateAccepted(e.target.checked)} colorScheme="orange" size="sm">
                    <Text fontSize="xs" color={mandateText} fontWeight="500">J'accepte le mandat de prélèvement SEPA</Text>
                </Checkbox>
            </Box>

            {formError && (
                <Alert status="error" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                </Alert>
            )}

            <Divider my={5} />

            <Button
                type="submit"
                colorScheme="orange"
                w="full"
                size="md"
                isLoading={loading}
                loadingText="Enregistrement du mandat…"
                isDisabled={isSubmitDisabled}
            >
                Valider le mandat
            </Button>

            <Text fontSize="xs" color="gray.400" textAlign="center" mt={3}>
                Vos données bancaires sont transmises de manière sécurisée vers Mollie
            </Text>
        </chakra.form>
    );
}

interface FieldErrors {
    consumerName: string | null;
    consumerAccount: string | null;
}

function isValidIBAN(iban: string): boolean {
    const cleaned = iban.replace(/\s/g, "").toUpperCase();
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,}$/.test(cleaned)) { return false; }

    const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
    const numeric = rearranged.split("").map((c) => (isNaN(Number(c)) ? (c.charCodeAt(0) - 55).toString() : c)).join("");

    let remainder = 0;
    for (const char of numeric) {
        remainder = (remainder * 10 + parseInt(char)) % 97;
    }
    return remainder === 1;
}

